import type { ToolBadgeSpec, ToolResultItem } from '@elara/client-core/chat/tool-badges/types';
import type { ToolCall } from '@elara/client-core/models/ToolCallModel';
import type { ToolCallStatus } from '@elara/domain/models/ToolCallSpec';

import type { IconName } from '#ui/MaterialIcon.js';

import { findToolBadgeSpec, isHiddenToolName } from '@elara/client-core/chat/tool-badges/registry';
import { resolveToolBadgeContent } from '@elara/client-core/chat/tool-badges/view-model';
import { cn } from '@elara/client-core/lib/utils';

import { useAppState, useUserProfileState } from '#states/hooks.js';
import { CollapsibleBlock } from '#ui/debug/collapsible-block.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

import { ExpandableResultsBadge } from './ExpandableResultsBadge.js';
import { ToolResultLinkList } from './ToolResultLinkList.js';

import { SimpleMarkdown } from '../SimpleMarkdown.js';

export interface ToolBadgeProps {
  toolCall: ToolCall;
  variant?: 'round' | 'square';
  className?: string;
}

export function ToolBadge({ toolCall, variant = 'round', className }: ToolBadgeProps) {
  const user = useUserProfileState();
  const app = useAppState();
  const spec = findToolBadgeSpec(toolCall.toolName);
  const hidden = isHiddenToolName(toolCall.toolName);

  // Unknown or hidden tool: render nothing for users; debug mode still shows a generic badge.
  if ((!spec || hidden) && !app.debugMode) return null;

  const isActive = toolCall.isActive;
  const isPrivileged = user.isPrivileged;
  const hasError = toolCall.extendedStatus === 'failed' && isPrivileged && toolCall.shortErrorMessage;

  const results =
    spec?.extractResults && toolCall.extendedStatus !== 'failed' && toolCall.output !== undefined
      ? spec.extractResults(toolCall)
      : undefined;
  const hasResults = results && results.items.length > 0;

  if (!app.debugMode || !user.isPrivileged) {
    if (hasError) return null;
    const content = resolveToolBadgeContent(toolCall);
    if (content.kind === 'hidden') return null;
    if (content.kind === 'expandable') {
      return (
        <ExpandableResultsBadge
          label={content.label}
          icon={content.icon}
          items={content.items}
          dataSource={content.dataSource}
          className={className}
        />
      );
    }
    return (
      <ToolBadgeBaseRow
        icon={content.icon}
        label={content.label}
        labelMarkdown={content.labelMarkdown}
        isActive={content.isActive}
      />
    );
  }

  return (
    <DebugToolBadge
      toolCall={toolCall}
      spec={spec}
      isActive={isActive}
      variant={variant}
      className={className}
      hasError={Boolean(hasError)}
      results={hasResults && results ? results.items : undefined}
    />
  );
}

function ToolBadgeBaseRow({
  icon,
  label,
  labelMarkdown,
  isActive,
}: {
  icon: IconName;
  label: string;
  labelMarkdown: boolean;
  isActive: boolean;
}) {
  const labelNode = labelMarkdown ? (
    <div className="flex items-center shortlist-activity-event flex-1 overflow-hidden">
      <SimpleMarkdown text={label} className="*:line-clamp-1" />
    </div>
  ) : (
    <span className="whitespace-nowrap truncate flex-1">{label}</span>
  );
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground/70 w-full">
      <MaterialIcon icon={icon} size={IconSizes.xs} className={cn('shrink-0', isActive && 'animate-spin')} />
      {labelNode}
    </div>
  );
}

/**
 * Debug/privileged-user rendering: wraps the badge in a CollapsibleBlock showing
 * the tool's result list (if any), its input, and its output or error stack.
 */
function DebugToolBadge({
  toolCall,
  spec,
  isActive,
  variant,
  className,
  hasError,
  results,
}: {
  toolCall: ToolCall;
  spec: ToolBadgeSpec | undefined;
  isActive: boolean;
  variant: 'round' | 'square';
  className?: string;
  hasError: boolean;
  results?: ToolResultItem[];
}) {
  const badgeStyle = badgeStyles[toolCall.extendedStatus];
  const labelText = spec ? spec.label(toolCall, isActive) : toolCall.toolName;
  const debugLabel = (
    <div className="flex items-center gap-2 w-full min-w-0">
      <span className="font-medium shrink-0">{labelText}</span>
      <span className="ml-auto text-[10px] font-mono text-muted-foreground/70 bg-muted/50 px-1.5 py-0.5 rounded truncate max-w-[240px]">
        {toolCall.id}
      </span>
    </div>
  );

  return (
    <CollapsibleBlock
      icon={isActive ? 'progress_activity' : toolCall.isWidget ? 'widgets' : (spec?.icon ?? 'build')}
      label={debugLabel}
      className={cn(
        'text-xs transition-all duration-200',
        badgeStyle.bg,
        variant === 'round' ? 'rounded-full' : 'rounded-md',
        className,
      )}
      labelClassName={badgeStyle.fg}
    >
      <div className="space-y-2 text-xs">
        {results && (
          <div>
            <div className="font-medium text-muted-foreground mb-1">Results ({results.length})</div>
            <div className="bg-background border rounded p-2">
              <ToolResultLinkList items={results} itemIcon={spec?.icon ?? 'build'} />
            </div>
          </div>
        )}
        {toolCall.input && (
          <div>
            <div className="font-medium text-muted-foreground mb-1">Input</div>
            <pre className="bg-background/50 border border-border/50 rounded p-2 overflow-auto max-h-48 text-[11px] whitespace-pre-wrap break-all">
              {JSON.stringify(toolCall.input, null, 2)}
            </pre>
          </div>
        )}
        {hasError ? (
          <div>
            <div className="font-medium text-red-500 mb-1">Error</div>
            <pre className="bg-red-500/5 border border-red-500/20 rounded p-2 overflow-auto max-h-48 text-[11px] text-red-600 dark:text-red-400 whitespace-pre-wrap break-all">
              {toolCall.error}
            </pre>
          </div>
        ) : toolCall.output !== undefined ? (
          <div>
            <div className="font-medium text-muted-foreground mb-1">Output</div>
            <pre className="bg-background/50 border border-border/50 rounded p-2 overflow-auto max-h-48 text-[11px] whitespace-pre-wrap break-all">
              {typeof toolCall.output === 'string' ? toolCall.output : JSON.stringify(toolCall.output, null, 2)}
            </pre>
          </div>
        ) : null}
      </div>
    </CollapsibleBlock>
  );
}

const badgeStyles = {
  pending: { bg: 'bg-blue-500/10 border-blue-500/50', fg: 'text-blue-500', icon: 'pending' },
  executing: { bg: 'bg-yellow-500/10 border-yellow-500/50', fg: 'text-yellow-500' },
  completed: { bg: 'bg-green-500/10 border-green-500/50', fg: 'text-green-500' },
  failed: { bg: 'bg-red-500/10 border-red-500/50', fg: 'text-red-500' },
  stuck: { bg: 'bg-red-300/10 border-red-300/50', fg: 'text-red-300' },
} as const satisfies Record<ToolCallStatus | 'stuck', BadgeStyle>;

interface BadgeStyle {
  bg: string;
  fg: string;
  icon?: IconName;
}
